import { pascalCase } from 'es-toolkit/string';

import { isUserVisibleException, ReadFile } from '@paragon/client-tools';
import { Point2D, SnappingUnderlay } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { VisibilityConfig } from '../../../library/hierarchy-filtering/visible-objects';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { findNextName } from '../../../library/utilities/findNextName';
import { focusCameraToVisibleSnappingGuideLines } from '../../../three/ThreeJsSceneActions';
import { applicationOnPickingLayoutPoint, clearPickingObject } from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { rightToolBarShowSet } from '../../slices/panelDataSlice';
import { snappingUnderlaySlice } from '../../slices/snappingUnderlaySlice';
import { setSnappingUnderlaysVisible } from '../../slices/visualOptionsSlice';
import { resetSelectedItems, selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';

const ERROR_SAVING_MESSAGE = 'Unable to save snapping underlays. Please try again or contact Paragon Customer Support.';

export const uploadDxfUnderlay =
  (file: ReadFile): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;

    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot upload an underlay outside of a project context.'));
      return;
    }

    const { projectGuid } = openContext;

    dispatch(snappingUnderlaySlice.actions.uploadBegan());

    try {
      const layouts = await designService.projects.extractDxfLayouts(projectGuid, file);

      if (layouts.length === 0) {
        dispatch(enqueueError('No geometry could be extracted from that DXF.'));
        dispatch(snappingUnderlaySlice.actions.uploadFailed());
        return;
      }

      dispatch(snappingUnderlaySlice.actions.dxfUploadCompleted({ fileName: file.filename, layouts }));
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(
          isUserVisibleException(error)
            ? error.userVisibleMessage
            : 'Unable to read that DXF file. Please try again or contact Paragon Customer Support.',
        ),
      );
      dispatch(snappingUnderlaySlice.actions.uploadFailed());
    }
  };

export const showSnappingUnderlay =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const hiddenByRenderMode = !getState().visualOptions.snappingUnderlaysVisible;

    if (hiddenByRenderMode) {
      dispatch(snappingUnderlaySlice.actions.showOnlyUnderlay(guid));
      dispatch(setSnappingUnderlaysVisible(true));
    } else {
      dispatch(snappingUnderlaySlice.actions.setUnderlayVisibility({ guid, visible: true }));
    }
  };

export const hideSnappingUnderlay =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(snappingUnderlaySlice.actions.setUnderlayVisibility({ guid, visible: false }));

    if (getSelectedItemIdentifiers(getState()).some((item) => item.type === 'snappingUnderlay' && item.guid === guid)) {
      dispatch(resetSelectedItems());
    }
  };

export const toggleSnappingUnderlay =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const currentlyVisible = VisibilityConfig.shouldBeVisible(
      guid,
      getState().snappingUnderlay.visibleUnderlayConfiguration,
    );

    if (currentlyVisible) {
      dispatch(hideSnappingUnderlay(guid));
    } else {
      dispatch(showSnappingUnderlay(guid));
    }
  };

export const exclusivelyShowSnappingUnderlay =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(snappingUnderlaySlice.actions.showOnlyUnderlay(guid));

    if (!getState().visualOptions.snappingUnderlaysVisible) {
      dispatch(setSnappingUnderlaysVisible(true));
    }
  };

export const startPickingUnderlayScale =
  (guid: string): ThunkActionVoid =>
  (dispatch) => {
    dispatch(snappingUnderlaySlice.actions.scalePickingStarted(guid));
    dispatch(snappingUnderlaySlice.actions.showOnlyUnderlay(guid));
    dispatch(applicationOnPickingLayoutPoint({ target: 'uploaded-lines-scale-base' }));
  };

export const cancelPickingUnderlayScale = (): ThunkActionVoid => (dispatch) => {
  dispatch(clearPickingObject());
  dispatch(snappingUnderlaySlice.actions.scalePickingCleared());
};

export const startPickingUnderlayOrigin =
  (guid: string): ThunkActionVoid =>
  (dispatch) => {
    dispatch(snappingUnderlaySlice.actions.showOnlyUnderlay(guid));
    dispatch(applicationOnPickingLayoutPoint({ target: 'uploaded-lines-origin' }));
  };

export const saveSnappingUnderlay =
  (underlay: SnappingUnderlay): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    dispatch(layoutSlice.actions.snappingUnderlaysUpserted([underlay]));

    const state = getState();
    if (state.application.openContext?.type !== 'project') return;

    const spinnerId = dispatch(addSpinnerMessage('Saving snapping underlay...'));

    try {
      await designService.projects.replaceSnappingUnderlays(
        state.application.openContext.projectGuid,
        state.layout.snappingUnderlays,
      );
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(ERROR_SAVING_MESSAGE));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const saveSnappingUnderlays = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const spinnerId = dispatch(addSpinnerMessage('Saving snapping underlays...'));

  try {
    await designService.projects.replaceSnappingUnderlays(
      state.application.openContext.projectGuid,
      state.layout.snappingUnderlays,
    );
  } catch (error) {
    console.error(error);
    dispatch(enqueueError(ERROR_SAVING_MESSAGE));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const persistNewSnappingUnderlay =
  (args: {
    extractedPaths: readonly (readonly Point2D[])[];
    scale: number;
    rotation: number;
    fileName: string;
    subIdentifier: string;
  }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let name = `${pascalCase(args.fileName)} ${args.subIdentifier}`;
    const existingNames = Object.values(state.layout.snappingUnderlays).map((u) => u.name);

    if (existingNames.includes(name)) {
      name = findNextName(existingNames, { prefix: name });
    }

    const underlay: SnappingUnderlay = {
      guid: crypto.randomUUID(),
      name,
      extractedPaths: args.extractedPaths,
      scale: args.scale,
      translate: { x: 0, y: 0 },
      rotation: args.rotation,
    };

    dispatch(snappingUnderlaySlice.actions.entirelyUnloaded());

    await dispatch(saveSnappingUnderlay(underlay));

    dispatch(selectItem(underlay, 'snappingUnderlay'));
    dispatch(rightToolBarShowSet(true));

    focusCameraToVisibleSnappingGuideLines();
  };
